import React from "react";
import styled, { ThemeProvider } from "styled-components";

import { DARK_THEME } from "../../theme/themes/darkTheme";
import { HexCustomerLogos } from "../signup/HexCustomerLogos";

import {
  OnboardingContainer,
  VERTICAL_BORDER_PADDING,
} from "./OnboardingElements";

const LoginContainer = styled(OnboardingContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  color: ${({ theme }) => theme.fontColor.DEFAULT};
`;

export const LoginHeader = styled.h1`
  flex: none;
  margin: 0;
  margin-bottom: 30px;

  color: ${({ theme }) => theme.marketingColors.OPAL};
  font-weight: 700;
  font-size: 38px;
  line-height: 44px;
  letter-spacing: -2px;
  text-align: center;

  > span {
    box-shadow: inset 0 -1px ${({ theme }) => theme.marketingColors.BACKGROUND};
  }
`;

export const LoginElements = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 390px;
  padding: 30px;
  height: 100%;
  min-height: 0;

  &:before,
  &:after {
    margin: auto;

    content: "";
  }

  @media (max-width: ${({ theme }) => `${theme.mediaQuery.SMALL}px`}) {
    padding: 0 15px;
  }
`;

const LoginContent = styled.div`
  display: flex;
  flex-grow: 1;
  padding: 0 ${VERTICAL_BORDER_PADDING}px;
  max-width: 1300px;
`;

const LoginWrappers = styled.div`
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const SuperText = styled.div`
  margin-top: 30px;

  color: ${({ theme }) => theme.marketingColors.ROSE_QUARTZ};
  font-size: ${({ theme }) => theme.fontSize.SMALL};

  font-family: ${({ theme }) => theme.fontFamily.MONO};
  line-height: 18px;
  text-align: center;
  text-transform: uppercase;
`;

export interface LoginPageProps {
  children?: React.ReactNode;
  orgName?: React.ReactNode;
  rightElement?: React.ReactNode;
  embedded: boolean;
  superText?: string;
  hideBackgroundBars?: boolean;
}

// For new users / new sign ups, we include a list of customer logos - we don't want to show this everywhere
export const LoginPagePropsForNewUser: Partial<LoginPageProps> = {
  hideBackgroundBars: true,
  rightElement: <HexCustomerLogos />,
};

export const LoginPage: React.ComponentType<LoginPageProps> = React.memo(
  function LoginPage({
    children,
    embedded,
    hideBackgroundBars,
    orgName,
    rightElement,
    superText,
  }) {
    const useOrgHeader = orgName != null;

    return (
      <ThemeProvider theme={DARK_THEME}>
        <LoginContainer hideBackgroundBars={hideBackgroundBars}>
          <LoginContent>
            <LoginWrappers>
              <LoginElements>
                {superText && <SuperText>{superText}</SuperText>}
                {!embedded && (
                  <>
                    {useOrgHeader ? (
                      <LoginHeader>{orgName}</LoginHeader>
                    ) : (
                      <LoginHeader>Log in</LoginHeader>
                    )}
                  </>
                )}
                {children}
              </LoginElements>
            </LoginWrappers>
            {rightElement}
          </LoginContent>
        </LoginContainer>
      </ThemeProvider>
    );
  },
);
